import helpers from '@/utils/helpers.ts'
import orderBy from 'lodash/orderBy'
import { clone, sortStringToObject } from '@deepcloud/deep-ui-utils'
import { useDeepBoxDeepBoxesBoxesInboxStore } from '@/stores/deepbox/deepboxes/boxes/inbox.ts'
import { useDeepBoxSearchStore } from '@/stores/deepbox/search.ts'
import { useApi } from '@/composables/api.ts'
import type {
  Node,
  NodeContent,
  NodeContentParams,
  PathSegment,
  SearchItem,
} from '@deepcloud/api-deep-box'

type RootNodes = {
  queue: PathSegment | undefined
  files: PathSegment | undefined
  trash: PathSegment | undefined
}

export type FetchNodesParams = {
  typeId: string
  boxId: string
  limit?: number
  offset?: number
  sortBy?: string[]
  refresh?: boolean
  includeHidden?: boolean
}

interface FetchNodesChildrenParams extends FetchNodesParams {
  nodeId: string
}

const abortController = ref<AbortController>()

export const useDeepBoxDeepBoxesBoxesNodesStore = defineStore(
  'deepBoxDeepBoxesBoxesNodesStore',
  () => {
    const { deepBoxAPI } = useApi()

    const fetchNodesPending = ref(false)
    const data = ref<NodeContent>()
    const params = ref<NodeContentParams>()
    const rootNodes = ref<RootNodes>({
      queue: undefined,
      files: undefined,
      trash: undefined,
    })

    const getCurrentRootNode = computed(() => {
      if (!data.value || data.value?.path?.segments.length === 0) {
        return null
      }
      return data.value?.path?.segments[data.value?.path?.segments.length - 1]
    })

    const getCurrentRootNodeId = computed(() => {
      if (!getCurrentRootNode.value) {
        return ''
      }
      return getCurrentRootNode.value?.nodeId
    })

    function _handleSetData(contentData: NodeContent, refresh?: boolean) {
      if (!data.value || refresh) {
        data.value = contentData
        return
      }

      if (!contentData.nodes) return

      // clone the saved nodes and merge it with contentData
      const nodes: Node[] = clone(data.value.nodes ?? [])
      for (const node of contentData.nodes) {
        const nodeInDataIndex = nodes.findIndex((n) => n.nodeId === node.nodeId)
        if (nodeInDataIndex > -1) {
          // replace the already present node with the one from contentData
          nodes.splice(nodeInDataIndex, 1, node)
          continue
        }
        nodes.push(node)
      }

      data.value = {
        ...contentData,
        nodes,
      }
    }

    function _handleNodeApiResponse(
      contentData: NodeContent,
      contentDataParams: NodeContentParams,
      refresh?: boolean,
    ) {
      // save the params
      params.value = contentDataParams

      _handleSetData(contentData, refresh)
    }

    function _handleApiParams(payload: FetchNodesParams) {
      let offset = payload.offset ?? data.value?.nodes?.length
      if (payload.refresh && data.value) {
        // if `refresh` always get the initial count ( limit + offset ) in order to get this amount of items
        // this make the API call faster... the user can still scroll down to load more items
        offset = 0
      }

      return {
        ...helpers.getFetchParameters({
          ...payload,
          offset,
        }),
        ...(payload.includeHidden
          ? { includeHidden: payload.includeHidden }
          : {}),
      }
    }

    function abortOrCreateNewAbortController() {
      if (abortController.value) {
        abortController.value.abort()
      }
      abortController.value = new AbortController()
    }

    function clearAbortController() {
      abortController.value = undefined
    }

    const deepBoxDeepBoxesBoxesInboxStore = useDeepBoxDeepBoxesBoxesInboxStore()

    async function fetchBoxInbox(payload: FetchNodesParams) {
      abortOrCreateNewAbortController()

      fetchNodesPending.value = true
      try {
        let paramsPayload = _handleApiParams(payload)

        if (deepBoxDeepBoxesBoxesInboxStore.hasFilterByBoxId(payload.boxId)) {
          const tagsString =
            deepBoxDeepBoxesBoxesInboxStore.getFilterByTagsStringByBoxId(
              payload.boxId,
            )

          paramsPayload = {
            ...paramsPayload,
            t: tagsString ? [tagsString] : undefined,
          }
        }

        const res = await deepBoxAPI.value.deepBoxesBoxesQueue.get(
          payload.typeId,
          payload.boxId,
          paramsPayload,
          {
            signal: abortController.value?.signal,
          },
        )

        // save the params
        params.value = paramsPayload

        _handleSetData(res.data, payload.refresh)

        // if there are nodes with analysisStatus = running, let's call the analyzeNode action.
        // this will update the relative analysisStatus property once the node has been analysed
        // TODO: this is tmp disabled unit the implementation of DEEPBOX-2822
        // res.data.nodes.forEach((node) => {
        //   if (node.analysisStatus === ANALYZE_STATUS.ANALYSIS_STATUS_RUNNING) {
        //     dispatch('analyzeNode', node);
        //   }
        // });

        clearAbortController()
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      } finally {
        fetchNodesPending.value = false
      }
    }

    async function fetchBoxFiles(payload: FetchNodesParams) {
      abortOrCreateNewAbortController()

      fetchNodesPending.value = true
      try {
        const paramsPayload = _handleApiParams(payload)

        const res = await deepBoxAPI.value.deepBoxesBoxesFiles.get(
          payload.typeId,
          payload.boxId,
          paramsPayload,
          {
            signal: abortController.value?.signal,
          },
        )

        _handleNodeApiResponse(res.data, paramsPayload, payload.refresh)

        clearAbortController()
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      } finally {
        fetchNodesPending.value = false
      }
    }

    async function fetchBoxFileNodes(payload: FetchNodesChildrenParams) {
      abortOrCreateNewAbortController()

      fetchNodesPending.value = true
      try {
        const paramsPayload = _handleApiParams(payload)

        const res = await deepBoxAPI.value.deepBoxesBoxesFiles.getById(
          payload.typeId,
          payload.boxId,
          payload.nodeId,
          paramsPayload,
          {
            signal: abortController.value?.signal,
          },
        )

        _handleNodeApiResponse(res.data, paramsPayload, payload.refresh)

        clearAbortController()
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      } finally {
        fetchNodesPending.value = false
      }
    }

    async function fetchBoxTrash(payload: FetchNodesParams) {
      abortOrCreateNewAbortController()

      fetchNodesPending.value = true
      try {
        const paramsPayload = _handleApiParams(payload)

        const res = await deepBoxAPI.value.deepBoxesBoxesTrash.get(
          payload.typeId,
          payload.boxId,
          paramsPayload,
          {
            signal: abortController.value?.signal,
          },
        )

        _handleNodeApiResponse(res.data, paramsPayload, payload.refresh)

        clearAbortController()
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      } finally {
        fetchNodesPending.value = false
      }
    }

    async function fetchBoxTrashNodes(payload: FetchNodesChildrenParams) {
      abortOrCreateNewAbortController()

      fetchNodesPending.value = true
      try {
        const paramsPayload = _handleApiParams(payload)

        const res = await deepBoxAPI.value.deepBoxesBoxesTrash.getById(
          payload.typeId,
          payload.boxId,
          payload.nodeId,
          paramsPayload,
          {
            signal: abortController.value?.signal,
          },
        )

        _handleNodeApiResponse(res.data, paramsPayload, payload.refresh)

        clearAbortController()
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      } finally {
        fetchNodesPending.value = false
      }
    }

    function updateNodeState(payload: Node) {
      if (!data.value?.nodes) return

      const nodes = data.value.nodes.map((node) =>
        node.nodeId === payload.nodeId ? { ...node, ...payload } : node,
      )

      data.value = {
        ...data.value,
        nodes,
      }
    }

    // function updateNodeState(payload: Node) {
    //   console.warn('updateNodeState ==> ', payload)
    //   if (!data.value) return
    //
    //   const nodes = [...data.value.nodes]
    //   const nodeIndex = nodes.findIndex(
    //     (node) => node.nodeId === payload.nodeId,
    //   )
    //
    //   if (nodeIndex !== -1) {
    //     // update existing node
    //     nodes[nodeIndex] = { ...nodes[nodeIndex], ...payload }
    //   } else {
    //     // add new node
    //     nodes.push(payload)
    //   }
    //
    //   data.value.nodes = nodes
    // }

    function removeNodeState(nodeId: string) {
      if (!data.value?.nodes) return

      const filteredNodes = data.value.nodes.filter(
        (node) => node.nodeId !== nodeId,
      )

      if (filteredNodes.length === data.value.nodes.length) return

      data.value = {
        ...data.value,
        nodes: filteredNodes,
        size: (data.value.size ?? data.value.nodes.length) - 1,
      }
    }

    function updateRootNode(pathSegment: PathSegment) {
      const segments = data.value?.path?.segments
      if (!segments) return

      const index = segments.findIndex((s) => s.nodeId === pathSegment.nodeId)
      if (index < 0) return

      data.value = {
        ...data.value,
        path: {
          ...data.value?.path,
          segments: segments.map((s, i) => (i === index ? pathSegment : s)),
        },
      }
    }

    function addNodeState(node: Node) {
      // SET NODE
      if (data.value && data.value?.nodes) {
        data.value.nodes.push(node)
        if (data.value?.size) {
          data.value.size += 1
        }

        if (!params.value) return
        // Sort nodes
        if (params.value?.order) {
          const order = sortStringToObject(params.value?.order)

          if (order && order.sortBy && order.sortBy.length > 0) {
            const desc = order.sortDesc?.[0] === true ? 'desc' : 'asc'
            const sorter = helpers.getSorter(order)
            data.value.nodes = orderBy(data.value.nodes, sorter, desc)
            data.value.nodes = orderBy(data.value.nodes, ['type'], 'desc')
          }
        }
      }
    }

    function getNodesByAnalysisStatus(analysisStatus: string) {
      if (!data.value?.nodes) return []
      return data.value.nodes.filter(
        (node) => node.analysisStatus === analysisStatus,
      )
    }

    const selectedNodeIds = ref<string[]>([])

    const deepBoxSearchStore = useDeepBoxSearchStore()

    const nodesByIdMap = computed(() => {
      const nodes = deepBoxSearchStore.searchResults?.items ?? data.value?.nodes
      const map = new Map<string, Node | SearchItem>()
      nodes?.forEach((node) => map.set(node.nodeId, node))
      return map
    })

    const selectedNodesMap = computed(() => {
      const map: Record<string, Node | SearchItem> = {}
      for (const nodeId of selectedNodeIds.value) {
        const node = nodesByIdMap.value.get(nodeId)
        if (node) map[nodeId] = node
      }
      return map
    })

    const selectedNodes = computed(
      () =>
        selectedNodeIds.value
          .map((nodeId) => nodesByIdMap.value.get(nodeId))
          .filter((node): node is Node | SearchItem => !!node) as
          Node[] | SearchItem[],
    )

    return {
      // state
      fetchNodesPending,
      data,
      params,
      rootNodes,
      // getters
      getCurrentRootNode,
      getCurrentRootNodeId,

      // selection
      selectedNodeIds,
      selectedNodesMap,
      selectedNodes,

      // actions
      // inbox
      fetchBoxInbox,
      // documents
      fetchBoxFiles,
      fetchBoxFileNodes,
      // trash
      fetchBoxTrash,
      fetchBoxTrashNodes,

      // helpers
      updateNodeState,
      removeNodeState,
      updateRootNode,
      addNodeState,
      getNodesByAnalysisStatus,
    }
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDeepBoxDeepBoxesBoxesNodesStore, import.meta.hot),
  )
}
